/**
 * Username rules, shared by the API, web and mobile.
 *
 * `slugifyUsername` is a deliberate mirror of the SQL `slugify_username()`
 * in supabase/migrations/20260730000001_user_usernames.sql. The two must
 * produce identical output — see username.test.ts, whose fixture table was
 * verified against the database directly. If you change one, change both.
 *
 * The database remains the enforcing authority for uniqueness, reserved
 * handles and tombstones; these helpers exist for fast client-side feedback
 * and for generating suggestions.
 */
import { isReservedUsername } from '../constants/reserved-usernames.js';
export const USERNAME_MIN_LENGTH = 3;
export const USERNAME_MAX_LENGTH = 30;
/** Mirrors the SQL CHECK constraint `users_username_format`. */
export const USERNAME_PATTERN = /^[a-z0-9][a-z0-9._-]{1,28}[a-z0-9]$/;
const CONSECUTIVE_SEPARATORS = /[._-]{2}/;
/**
 * Accented Latin → ASCII. Kept identical to the SQL translate() pair so a
 * name like "José Ángel Núñez" slugs to "jose.angel.nunez" on both sides
 * rather than losing its accented characters entirely.
 */
const TRANSLITERATE_FROM = 'áàâäãåāăąçćčĉċđďéèêëēĕėęěğģĝġíìîïīĭįıĺľłñńņňóòôöõøōŏőśşšŝúùûüūŭůűųýÿŷźżžŕřţťĵķŵ';
const TRANSLITERATE_TO = 'aaaaaaaaacccccddeeeeeeeeeggggiiiiiiiilllnnnnooooooooossssuuuuuuuuuyyyzzzrrttjkw';
/** Multi-character expansions, applied before the 1:1 map. */
const EXPANSIONS = [
    ['ß', 'ss'],
    ['æ', 'ae'],
    ['œ', 'oe'],
    ['þ', 'th'],
];
function transliterate(input) {
    let out = input;
    for (const [from, to] of EXPANSIONS)
        out = out.split(from).join(to);
    let result = '';
    for (const char of out) {
        const idx = TRANSLITERATE_FROM.indexOf(char);
        result += idx === -1 ? char : TRANSLITERATE_TO[idx];
    }
    return result;
}
/**
 * Derive a candidate handle from arbitrary text (usually a display name).
 *
 * Returns `''` for input with no Latin alphanumerics at all (CJK, emoji) —
 * callers own the fallback chain, exactly as the SQL generator does.
 * Output is capped at 27 chars to leave room for a `.NNN` collision suffix.
 */
export function slugifyUsername(input) {
    return transliterate((input ?? '').toLowerCase())
        .replace(/[^a-z0-9]+/g, '.') // runs of non-alphanumerics collapse to one dot
        .replace(/^\.+|\.+$/g, '') // no leading/trailing dots
        .slice(0, 27)
        .replace(/\.+$/g, ''); // re-strip a dot exposed by the cap
}
/**
 * Validate a handle the user typed. Returns null when it is acceptable.
 * Uniqueness is NOT checked here — that needs the server.
 */
export function checkUsername(candidate) {
    const value = candidate.trim().toLowerCase();
    if (value.length < USERNAME_MIN_LENGTH)
        return 'too_short';
    if (value.length > USERNAME_MAX_LENGTH)
        return 'too_long';
    if (CONSECUTIVE_SEPARATORS.test(value))
        return 'consecutive_separators';
    if (!USERNAME_PATTERN.test(value))
        return 'invalid_characters';
    if (isReservedUsername(value))
        return 'reserved';
    return null;
}
export function isValidUsername(candidate) {
    return checkUsername(candidate) === null;
}
/** Human-readable reason, for form validation messages. */
export const USERNAME_PROBLEM_MESSAGES = {
    too_short: `Must be at least ${USERNAME_MIN_LENGTH} characters.`,
    too_long: `Must be ${USERNAME_MAX_LENGTH} characters or fewer.`,
    invalid_characters: 'Use lowercase letters, numbers, dots, underscores and hyphens. Must start and end with a letter or number.',
    consecutive_separators: 'Cannot contain two dots, underscores or hyphens in a row.',
    reserved: 'That username is reserved.',
};
/** Days a user must wait between username changes. Enforced by the API. */
export const USERNAME_CHANGE_COOLDOWN_DAYS = 30;
//# sourceMappingURL=username.js.map